import { z } from 'zod';

import { AccountStatusSchema, KycStatusSchema, RoleSchema } from '../common/enums';
import {
  Email,
  Id,
  IsoDateTime,
  Password,
  PhoneIN,
  Pincode,
  PlainText,
} from '../common/primitives';

// Address free-text fields are HTML-stripped at write time (`PlainText`): they
// get rendered outside React's escaping in at least one place (the storefront's
// printable invoice), and it is safer to make that field HTML-free everywhere
// than to rely on every downstream renderer escaping it correctly.
export const AddressSchema = z.object({
  id: Id,
  label: PlainText(40).optional(), // "Home", "Office"
  contactName: PlainText(120, 2),
  contactPhone: PhoneIN,
  line1: PlainText(200, 3),
  line2: PlainText(200).optional(),
  landmark: PlainText(120).optional(),
  city: PlainText(80, 2),
  state: PlainText(80, 2),
  pincode: Pincode,
  country: z.literal('IN').default('IN'),
  isDefault: z.boolean().default(false),
});
export type Address = z.infer<typeof AddressSchema>;

export const AddressInput = AddressSchema.omit({ id: true });
export type AddressInput = z.infer<typeof AddressInput>;

export const UserProfileSchema = z.object({
  id: Id,
  name: z.string(),
  email: Email,
  phone: z.string(),
  roles: z.array(RoleSchema),
  status: AccountStatusSchema,
  emailVerified: z.boolean(),
  phoneVerified: z.boolean(),
  kycStatus: KycStatusSchema,
  avatarUrl: z.string().url().nullable().optional(),
  adminRoleId: z.string().nullable().optional(),
  createdAt: IsoDateTime,
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

export const UpdateProfileRequest = z
  .object({
    name: PlainText(120, 2).optional(),
    avatarFileId: Id.optional(),
  })
  .strict();
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequest>;

// ── Account security (self-service) ────────────────────────────────────────
//
// Everything here changes something an attacker would want to change, so
// every request carries the current password again. A stolen access token is
// not enough to lock the real owner out.

/**
 * Change the signed-in user's password. On success every *other* session is
 * revoked: whoever knew the old password no longer has a way in.
 */
export const ChangePasswordRequest = z
  .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: Password,
  })
  .strict()
  .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'New password must differ from the current one',
    path: ['newPassword'],
  });
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequest>;

/**
 * Step 1 of changing a contact detail: prove the password, name the new
 * address, and a code goes *to the new address*. Nothing is saved yet.
 *
 * The code goes to the new destination, not the old one, because the point is
 * to prove the owner can receive at it. An admin's sign-in code is delivered to
 * their phone; letting a typo be saved without proof would lock them out.
 */
export const StartContactChangeRequest = z
  .object({
    currentPassword: z.string().min(1).max(128),
    channel: z.enum(['SMS', 'EMAIL']),
    /** The new phone (for SMS) or new email (for EMAIL). */
    value: z.union([Email, PhoneIN]),
  })
  .strict();
export type StartContactChangeRequest = z.infer<typeof StartContactChangeRequest>;

export const StartContactChangeResponse = z.object({
  /** Opaque, short-lived; hand it back to `/me/contact/verify`. */
  challengeToken: z.string(),
  expiresIn: z.number().int().positive(),
  /** Masked new destination, for display. */
  sentTo: z.string(),
});
export type StartContactChangeResponse = z.infer<typeof StartContactChangeResponse>;

/** Step 2: the code proves the new address is the caller's; only then is it saved. */
export const VerifyContactChangeRequest = z
  .object({
    challengeToken: z.string().min(20),
    code: z.string().regex(/^\d{6}$/),
  })
  .strict();
export type VerifyContactChangeRequest = z.infer<typeof VerifyContactChangeRequest>;

/** One signed-in device, as the owner sees it. */
export const SessionSummarySchema = z.object({
  id: Id,
  /** True for the session making this request, so the UI can mark "this device". */
  current: z.boolean(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: IsoDateTime,
  expiresAt: IsoDateTime,
});
export type SessionSummary = z.infer<typeof SessionSummarySchema>;

/** KYC submission — file ids reference objects already uploaded via the files module. */
export const KycSubmitRequest = z
  .object({
    legalName: z.string().trim().min(2).max(200),
    pan: z.string(), // validated server-side with the PAN primitive; kept loose here so the API can normalise
    gstin: z.string().optional(),
    bankAccountName: z.string().trim().min(2).max(200),
    bankAccountNumber: z.string().regex(/^\d{6,20}$/),
    bankIfsc: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/),
    panDocFileId: Id,
    addressProofFileId: Id,
    gstDocFileId: Id.optional(),
    upiId: z.string().trim().max(120).optional(),
    payoutPreference: z.enum(['BANK', 'UPI']).optional(),
  })
  .strict();
export type KycSubmitRequest = z.infer<typeof KycSubmitRequest>;

/* admin */
export const SetUserRoleRequest = z.object({ roles: z.array(RoleSchema).min(1) }).strict();
export type SetUserRoleRequest = z.infer<typeof SetUserRoleRequest>;

export const CreateUserRequest = z
  .object({
    name: z.string().trim().min(2).max(120),
    email: z.string().email(),
    phone: PhoneIN,
    password: z.string().min(8).max(100),
    roles: z.array(RoleSchema).default(['CUSTOMER']),
    adminRoleId: z.string().nullable().optional(),
  })
  .strict();
export type CreateUserRequest = z.infer<typeof CreateUserRequest>;

export const UpdateUserRequest = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    email: z.string().email().optional(),
    phone: PhoneIN.optional(),
    password: z.string().min(8).max(100).optional(),
    adminRoleId: z.string().nullable().optional(),
  })
  .strict();
export type UpdateUserRequest = z.infer<typeof UpdateUserRequest>;
